import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "../../components/app/AppShell";
import { useAuth } from "../../lib/auth";
import { BETA_MODE } from "../../lib/beta";
import { STUB_INVOICES, type StubInvoice } from "../../lib/stubs";
import { getProfile, listProfiles, requestWiderruf } from "../../lib/api";
import { VITANARA_CONTACT_EMAIL } from "../../lib/contact";

// Konto · Abo + Rechnungen. Invoice data is stubbed via STUB_INVOICES.
// TODO(Phase C): replace stubs with /me/account, /me/invoices.

const BASE_PRICE_EUR = 149;

function formatEUR(amount: number): string {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export default function AppAccount() {
  const { user, signOutNow } = useAuth();
  const navigate = useNavigate();
  const [language] = useState("Deutsch");
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifyPush, setNotifyPush] = useState(false);
  const recent: StubInvoice[] = STUB_INVOICES.slice(0, 3);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [code, setCode] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [widerrufState, setWiderrufState] = useState<
    "idle" | "sending" | "done" | "error"
  >("idle");

  useEffect(() => {
    (async () => {
      try {
        const r = await listProfiles();
        const p = (r.profiles || [])[0];
        if (!p) return;
        setPhone(p.phone);
        const detail = await getProfile(p.phone);
        setDiscountPercent(detail.code_discount_percent || 0);
        setCode(detail.friends_family_code || "");
      } catch {
        /* ignore — discount line just won't render */
      }
    })();
  }, []);

  const discountAmount = (BASE_PRICE_EUR * discountPercent) / 100;
  const finalPrice = BASE_PRICE_EUR - discountAmount;
  const hasDiscount = discountPercent > 0;

  async function handleSignOut() {
    try {
      await signOutNow();
    } finally {
      navigate("/login");
    }
  }

  // Widerruf: recorded server-side (durable record + confirmation email).
  // The mailto below is only a fallback if the request fails.
  const widerrufMailto =
    `mailto:${VITANARA_CONTACT_EMAIL}` +
    `?subject=${encodeURIComponent("Widerruf meines Vitanara-Vertrags")}` +
    `&body=${encodeURIComponent(
      "Hiermit widerrufe ich den von mir abgeschlossenen Vertrag über die " +
        "Dienstleistungen von Vitanara.\n\n" +
        `Konto-E-Mail: ${user?.email || "(bitte ergänzen)"}\n` +
        "Datum des Vertragsschlusses: (bitte ergänzen)\n\n" +
        "Mit freundlichen Grüßen",
    )}`;

  async function handleWiderruf() {
    if (widerrufState === "sending" || widerrufState === "done") return;
    const ok = window.confirm(
      "Möchten Sie Ihren Vertrag wirklich widerrufen? Wir bestätigen den " +
        "Eingang per E-Mail.",
    );
    if (!ok) return;
    setWiderrufState("sending");
    try {
      await requestWiderruf(phone || undefined);
      setWiderrufState("done");
    } catch {
      setWiderrufState("error");
    }
  }

  return (
    <AppShell
      eyebrow="Konto"
      title={
        <>
          Mein <em>Abo</em>
        </>
      }
    >
      <div style={{ padding: "0 22px 90px" }}>
        {/* Active package banner */}
        <div
          style={{
            background: "var(--paper-50)",
            border: "1.5px solid var(--oxblood)",
            borderRadius: 14,
            padding: "18px 20px",
            marginBottom: 18,
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: -8,
              right: 18,
              background: "var(--oxblood)",
              color: "var(--paper-50)",
              fontFamily: "var(--f-ui)",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              padding: "4px 10px",
              borderRadius: 4,
            }}
          >
            aktiv
          </div>
          <div
            style={{
              fontFamily: "var(--f-display)",
              fontStyle: "italic",
              fontSize: 22,
              color: "var(--ink)",
            }}
          >
            Basis {BETA_MODE && "(Beta)"}
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 4 }}>
            <span
              style={{
                fontFamily: "var(--f-display)",
                fontWeight: 500,
                fontSize: 26,
                color: hasDiscount ? "var(--ink-faint)" : "var(--oxblood)",
                textDecoration: hasDiscount ? "line-through" : "none",
              }}
            >
              {formatEUR(BASE_PRICE_EUR)}
            </span>
            <span
              style={{
                fontFamily: "var(--f-body)",
                fontStyle: "italic",
                fontSize: 13,
                color: "var(--ink-faint)",
              }}
            >
              einmalig
            </span>
          </div>

          {hasDiscount && (
            <div
              style={{
                marginTop: 10,
                paddingTop: 10,
                borderTop: "1px dashed rgba(43,26,15,0.18)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  fontFamily: "var(--f-body)",
                  fontSize: 13.5,
                  color: "var(--sage)",
                }}
              >
                <span>
                  Aktionscode{" "}
                  <strong
                    style={{
                      fontFamily: "ui-monospace, monospace",
                      fontStyle: "normal",
                      letterSpacing: "0.06em",
                      color: "var(--ink)",
                    }}
                  >
                    {code || "—"}
                  </strong>{" "}
                  ({discountPercent} %)
                </span>
                <span>− {formatEUR(discountAmount)}</span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  marginTop: 8,
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--f-ui)",
                    textTransform: "uppercase",
                    letterSpacing: "0.16em",
                    fontSize: 11,
                    color: "var(--ink-faint)",
                  }}
                >
                  Gesamt
                </span>
                <span
                  style={{
                    fontFamily: "var(--f-display)",
                    fontWeight: 500,
                    fontSize: 22,
                    color: "var(--oxblood)",
                  }}
                >
                  {formatEUR(finalPrice)}
                </span>
              </div>
            </div>
          )}

          {BETA_MODE && (
            <div
              style={{
                marginTop: 10,
                display: "inline-block",
                padding: "4px 10px",
                background: "rgba(168,122,58,0.18)",
                borderRadius: 4,
                fontFamily: "var(--f-ui)",
                fontSize: 10,
                fontWeight: 700,
                color: "var(--oxblood)",
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                transform: "rotate(-1.5deg)",
              }}
            >
              Im Beta enthalten
            </div>
          )}
        </div>

        {/* Payment method — stubbed visual only */}
        <ProfileSection title="Zahlungsart">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "14px 16px",
            }}
          >
            <div
              aria-hidden
              style={{
                width: 40,
                height: 26,
                background: "linear-gradient(135deg, #1A1F71, #4256C7)",
                borderRadius: 4,
                flexShrink: 0,
              }}
            />
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontFamily: "var(--f-display)",
                  fontWeight: 500,
                  fontSize: 14,
                  color: "var(--ink)",
                }}
              >
                Visa endend auf 4242
              </div>
              <div
                style={{
                  fontFamily: "var(--f-body)",
                  fontStyle: "italic",
                  fontSize: 12,
                  color: "var(--ink-faint)",
                }}
              >
                läuft 05/29 aus
              </div>
            </div>
            <button
              type="button"
              // TODO(Phase C): open Stripe Customer Portal.
              style={{
                background: "transparent",
                border: "none",
                fontFamily: "var(--f-ui)",
                fontSize: 12,
                fontWeight: 600,
                color: "var(--oxblood)",
                cursor: "pointer",
              }}
            >
              Ändern
            </button>
          </div>
        </ProfileSection>

        {/* Recent invoices */}
        <div style={{ marginBottom: 18 }}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <div className="t-caps">Rechnungen</div>
            <Link
              to="/app/invoices"
              style={{
                fontFamily: "var(--f-ui)",
                fontSize: 11,
                fontWeight: 600,
                color: "var(--oxblood)",
                textTransform: "uppercase",
                letterSpacing: "0.10em",
                textDecoration: "none",
              }}
            >
              Alle anzeigen →
            </Link>
          </div>
          <div
            style={{
              background: "var(--paper-50)",
              border: "1px solid rgba(43,26,15,0.10)",
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            {recent.map((inv, i) => (
              <div
                key={inv.nr}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 16px",
                  borderBottom:
                    i < recent.length - 1
                      ? "1px solid rgba(43,26,15,0.08)"
                      : "none",
                }}
              >
                <PdfThumb />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontFamily: "var(--f-display)",
                      fontWeight: 500,
                      fontSize: 14,
                      color: "var(--ink)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {inv.title}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 8,
                      fontFamily: "var(--f-body)",
                      fontStyle: "italic",
                      fontSize: 12,
                      color: "var(--ink-faint)",
                    }}
                  >
                    <span>{inv.date}</span>
                    <span style={{ opacity: 0.5 }}>·</span>
                    <span
                      style={{
                        fontFamily: "ui-monospace, monospace",
                        fontStyle: "normal",
                        fontSize: 10,
                      }}
                    >
                      {inv.nr}
                    </span>
                  </div>
                  <span
                    style={{
                      display: "inline-block",
                      marginTop: 3,
                      padding: "1px 6px",
                      borderRadius: 3,
                      background: "rgba(95,107,79,0.18)",
                      color: "#3F4A33",
                      fontFamily: "var(--f-ui)",
                      fontSize: 8.5,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                    }}
                  >
                    {inv.status === "paid" ? "Bezahlt" : "Offen"}
                  </span>
                </div>
                <div
                  style={{
                    fontFamily: "var(--f-display)",
                    fontSize: 14,
                    color: "var(--ink)",
                    flexShrink: 0,
                    textAlign: "right",
                  }}
                >
                  {inv.total}
                </div>
                <button
                  type="button"
                  // TODO(Phase C): wire signed-URL PDF download.
                  aria-label={`Rechnung ${inv.nr} herunterladen`}
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 8,
                    background: "var(--oxblood)",
                    border: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    cursor: "pointer",
                  }}
                >
                  <DownloadIcon />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Account / language / notifications */}
        <ProfileSection title="Konto">
          <ProfileRow k="E-Mail" v={user?.email || "—"} />
          <ProfileRow k="Sprache" v={language} />
          <div
            style={{
              padding: "12px 16px",
              borderTop: "1px solid rgba(43,26,15,0.08)",
            }}
          >
            <div
              style={{
                fontFamily: "var(--f-ui)",
                fontSize: 12,
                color: "var(--ink-faint)",
                textTransform: "uppercase",
                letterSpacing: "0.10em",
                marginBottom: 8,
              }}
            >
              Benachrichtigungen
            </div>
            <ToggleRow
              label="Per E-Mail"
              on={notifyEmail}
              onToggle={() => setNotifyEmail((v) => !v)}
            />
            <ToggleRow
              label="Push"
              on={notifyPush}
              onToggle={() => setNotifyPush((v) => !v)}
            />
          </div>
        </ProfileSection>

        {/* Widerruf (14-day right of withdrawal, AGB § 8) */}
        <ProfileSection title="Widerruf">
          <div style={{ padding: "14px 16px" }}>
            <p
              style={{
                margin: "0 0 12px",
                fontFamily: "var(--f-body)",
                fontSize: 13.5,
                lineHeight: 1.5,
                color: "var(--ink-soft)",
              }}
            >
              Als Verbraucher können Sie Ihren Vertrag innerhalb von 14 Tagen
              ohne Angabe von Gründen widerrufen. Wir bestätigen den Eingang
              per E-Mail. Einzelheiten in den{" "}
              <Link
                to="/agb"
                style={{ color: "var(--oxblood)", textDecoration: "none" }}
              >
                AGB (§ 8)
              </Link>
              .
            </p>

            {widerrufState === "done" ? (
              <div
                style={{
                  padding: "12px 14px",
                  background: "rgba(95,107,79,0.14)",
                  border: "1px solid rgba(95,107,79,0.35)",
                  borderRadius: 10,
                  fontFamily: "var(--f-body)",
                  fontSize: 13.5,
                  lineHeight: 1.5,
                  color: "#3F4A33",
                }}
              >
                Ihr Widerruf ist eingegangen. Eine Bestätigung wurde an{" "}
                <strong>{user?.email || "Ihre E-Mail-Adresse"}</strong>{" "}
                geschickt.
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleWiderruf}
                  disabled={widerrufState === "sending"}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "12px 16px",
                    background: "transparent",
                    border: "1.5px solid var(--oxblood)",
                    borderRadius: 10,
                    fontFamily: "var(--f-ui)",
                    fontSize: 14,
                    fontWeight: 600,
                    color: "var(--oxblood)",
                    cursor: widerrufState === "sending" ? "default" : "pointer",
                    opacity: widerrufState === "sending" ? 0.6 : 1,
                  }}
                >
                  {widerrufState === "sending"
                    ? "Wird gesendet …"
                    : "Vertrag widerrufen"}
                </button>
                {widerrufState === "error" && (
                  <p
                    role="alert"
                    style={{
                      margin: "10px 0 0",
                      fontFamily: "var(--f-body)",
                      fontSize: 13,
                      lineHeight: 1.5,
                      color: "var(--error)",
                    }}
                  >
                    Der Widerruf konnte nicht gesendet werden. Bitte versuchen
                    Sie es erneut oder schicken Sie ihn direkt per{" "}
                    <a
                      href={widerrufMailto}
                      style={{
                        color: "var(--oxblood)",
                        textDecoration: "underline",
                      }}
                    >
                      E-Mail an {VITANARA_CONTACT_EMAIL}
                    </a>
                    .
                  </p>
                )}
              </>
            )}
          </div>
        </ProfileSection>

        <button
          type="button"
          onClick={handleSignOut}
          style={{
            display: "block",
            width: "100%",
            margin: "6px 0 4px",
            padding: 0,
            background: "transparent",
            border: "none",
            fontFamily: "var(--f-ui)",
            fontSize: 12.5,
            fontWeight: 600,
            color: "var(--oxblood)",
            textAlign: "center",
            textDecoration: "underline",
            textUnderlineOffset: 3,
            cursor: "pointer",
          }}
        >
          Abmelden
        </button>
      </div>
    </AppShell>
  );
}

function ProfileSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div className="t-caps" style={{ marginBottom: 8 }}>
        {title}
      </div>
      <div
        style={{
          background: "var(--paper-50)",
          border: "1px solid rgba(43,26,15,0.10)",
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function ProfileRow({ k, v }: { k: string; v: string }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        padding: "12px 16px",
        borderBottom: "1px solid rgba(43,26,15,0.08)",
        gap: 12,
      }}
    >
      <div
        style={{
          fontFamily: "var(--f-ui)",
          fontSize: 12,
          color: "var(--ink-faint)",
          textTransform: "uppercase",
          letterSpacing: "0.10em",
        }}
      >
        {k}
      </div>
      <div
        style={{
          fontFamily: "var(--f-display)",
          fontSize: 15,
          color: "var(--ink)",
          textAlign: "right",
          maxWidth: "60%",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {v}
      </div>
    </div>
  );
}

function ToggleRow({
  label,
  on,
  onToggle,
}: {
  label: string;
  on: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 0",
      }}
    >
      <div
        style={{
          fontFamily: "var(--f-body)",
          fontSize: 14,
          color: "var(--ink)",
        }}
      >
        {label}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        onClick={onToggle}
        style={{
          width: 38,
          height: 22,
          borderRadius: 999,
          background: on ? "var(--oxblood)" : "rgba(43,26,15,0.18)",
          border: "none",
          position: "relative",
          cursor: "pointer",
          transition: "background 120ms ease-out",
          padding: 0,
        }}
      >
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: 2,
            left: on ? 18 : 2,
            width: 18,
            height: 18,
            borderRadius: "50%",
            background: "var(--paper-50)",
            transition: "left 120ms ease-out",
            boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
          }}
        />
      </button>
    </div>
  );
}

function PdfThumb() {
  return (
    <div
      aria-hidden
      style={{
        width: 36,
        height: 44,
        borderRadius: 4,
        background: "var(--paper-200)",
        border: "1px solid rgba(43,26,15,0.14)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        position: "relative",
      }}
    >
      <span
        style={{
          fontFamily: "var(--f-ui)",
          fontSize: 8,
          fontWeight: 700,
          color: "var(--oxblood)",
          letterSpacing: "0.10em",
        }}
      >
        PDF
      </span>
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 0,
          height: 0,
          borderLeft: "6px solid transparent",
          borderBottom: "6px solid var(--paper-50)",
        }}
      />
    </div>
  );
}

function DownloadIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--paper-50)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <path d="M7 10l5 5 5-5" />
      <path d="M12 15V3" />
    </svg>
  );
}
